import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { AdminPageLayout } from "@/layouts/AdminPageLayout";
import {
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui";
import { useAppToast } from "@/hooks/useAppToast";
import {
  getStudentById,
  getStudentPackageModules,
  deleteStudentModuleTestSessions,
  resetModuleAnswers,
  setStudentModuleLock,
} from "@/services/admin/student.service";
import { ModuleTestProgress, type StudentPackageModule } from "@/services/admin/common.interfaces";
import { StudentPackageAccessSection } from "@/components/admin/StudentPackageAccessSection";

const devWarnUnknownModuleTestProgress = (raw: unknown, moduleTitle: string): void => {
  if (process.env.NODE_ENV !== "development") return;
  // eslint-disable-next-line no-console
  console.warn(`[StudentPackageManagement] Unknown moduleTestProgress for "${moduleTitle}":`, raw);
};

const StudentPackageManagementPage: React.FC = () => {
  const { studentId, packageId } = useParams<{ studentId: string; packageId: string }>();
  const navigate = useNavigate();
  const toast = useAppToast();
  const [packageName, setPackageName] = useState<string>("");
  const [modules, setModules] = useState<StudentPackageModule[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [moduleToReset, setModuleToReset] = useState<StudentPackageModule | null>(null);
  const [isResetting, setIsResetting] = useState(false);
  const [removeTestModalOpen, setRemoveTestModalOpen] = useState(false);
  const [moduleToRemoveTest, setModuleToRemoveTest] = useState<StudentPackageModule | null>(null);
  const [isRemovingTest, setIsRemovingTest] = useState(false);
  const [lockTogglingModuleId, setLockTogglingModuleId] = useState<string | null>(null);
  /** When opening "Remover teste" from the dropdown, skip menu focus return (avoids Radix menu+dialog focus/pointer-event bugs). */
  const skipTestMenuCloseFocus = useRef(false);

  const loadStudentAndPackageNames = useCallback(async () => {
    if (!studentId || !packageId) return;
    try {
      const student = await getStudentById(studentId);
      const pkg = student.packages?.find((p) => p.id === packageId);
      setPackageName(pkg?.name ?? "Package");
    } catch {
      setPackageName("Package");
    }
  }, [studentId, packageId]);

  const loadModules = useCallback(async () => {
    if (!studentId || !packageId) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const list = await getStudentPackageModules(studentId, packageId);
      const sorted = [...list].sort((a, b) => a.order - b.order);
      setModules(sorted);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erro ao carregar módulos");
      setModules([]);
    } finally {
      setIsLoading(false);
    }
  }, [studentId, packageId]);

  useEffect(() => {
    loadStudentAndPackageNames();
  }, [loadStudentAndPackageNames]);

  useEffect(() => {
    loadModules();
  }, [loadModules]);

  const handleResetClick = (moduleItem: StudentPackageModule) => {
    setModuleToReset(moduleItem);
    setResetModalOpen(true);
  };

  const handleResetConfirm = async () => {
    if (!studentId || !moduleToReset) return;
    const resetModuleId = moduleToReset.moduleId;
    setIsResetting(true);
    try {
      await resetModuleAnswers(studentId, resetModuleId);
      toast.success("Respostas do módulo resetadas com sucesso.");
      setResetModalOpen(false);
      setModuleToReset(null);
      setModules((prev) =>
        prev.map((m) =>
          m.moduleId === resetModuleId
            ? {
                ...m,
                progressPercentage: 0,
                exercisesAnswered: 0,
                lastActivityAt: null,
                sessionStatus: undefined,
              }
            : m
        )
      );
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Falha ao resetar respostas.");
    } finally {
      setIsResetting(false);
    }
  };

  const handleResetCancel = () => {
    setResetModalOpen(false);
    setModuleToReset(null);
  };

  const handleRemoveTestClick = (moduleItem: StudentPackageModule) => {
    setModuleToRemoveTest(moduleItem);
    setRemoveTestModalOpen(true);
  };

  const handleRemoveTestCancel = () => {
    setRemoveTestModalOpen(false);
    setModuleToRemoveTest(null);
  };

  const handleRemoveTestConfirm = async () => {
    if (!studentId || !moduleToRemoveTest) return;
    setIsRemovingTest(true);
    try {
      await deleteStudentModuleTestSessions(studentId, moduleToRemoveTest.moduleId);
      toast.success("Teste do módulo removido com sucesso.");
      setRemoveTestModalOpen(false);
      setModuleToRemoveTest(null);
      await loadModules();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Falha ao remover teste do módulo.");
    } finally {
      setIsRemovingTest(false);
    }
  };

  const handleLockToggle = async (mod: StudentPackageModule, nextUnlocked: boolean) => {
    if (!studentId || !packageId) return;
    setLockTogglingModuleId(mod.moduleId);
    try {
      await setStudentModuleLock(studentId, packageId, mod.moduleId, nextUnlocked);
      setModules((prev) =>
        prev.map((m) =>
          m.moduleId === mod.moduleId ? { ...m, isUnlocked: nextUnlocked } : m
        )
      );
      toast.success(nextUnlocked ? "Módulo liberado." : "Módulo bloqueado.");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Falha ao alterar bloqueio do módulo.");
    } finally {
      setLockTogglingModuleId(null);
    }
  };

  const formatLastActivity = (lastActivityAt: string | null): string => {
    if (!lastActivityAt) return "—";
    try {
      const date = new Date(lastActivityAt);
      return date.toLocaleDateString(undefined, {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "—";
    }
  };

  const formatProgress = (progressPercentage: number | null | undefined): number => {
    if (typeof progressPercentage !== "number" || Number.isNaN(progressPercentage)) return 0;
    return Math.min(100, Math.max(0, Math.round(progressPercentage)));
  };

  const unlockedModulesCount = modules.filter((moduleItem) => moduleItem.isUnlocked).length;

  if (!studentId || !packageId) {
    return (
      <AdminPageLayout title="Gestão de pacote">
        <main id="main-content" className="w-full px-4 py-8 sm:px-6">
          <div className="mb-6 flex items-center">
            <button
              type="button"
              onClick={() => navigate("/admin/access/students")}
              className="flex min-h-[44px] min-w-[44px] items-center justify-center gap-2 rounded-md px-4 text-sm font-medium text-neutral-800 hover:bg-neutral-100 hover:text-neutral-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
            >
              <i className="fa-solid fa-arrow-left" aria-hidden />
              <span>Voltar aos estudantes</span>
            </button>
          </div>
          <div className="rounded-lg border border-neutral-200 bg-white p-6 shadow-sm">
            <p className="text-sm text-neutral-800">Parâmetros inválidos.</p>
            <Button variant="outline" className="mt-4 min-h-[44px] px-4" onClick={() => navigate("/admin/access/students")}>
              Voltar aos estudantes
            </Button>
          </div>
        </main>
      </AdminPageLayout>
    );
  }

  return (
    <AdminPageLayout title={`Módulos do pacote – ${packageName}`}>
      <main id="main-content" className="w-full px-4 py-8 sm:px-6">
        <div className="mb-6 flex items-center">
          <button
            type="button"
            onClick={() => navigate(`/admin/access/students/${studentId}`)}
            className="flex min-h-[44px] min-w-[44px] items-center justify-center gap-2 rounded-md px-4 text-sm font-medium text-neutral-800 hover:bg-neutral-100 hover:text-neutral-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
          >
            <i className="fa-solid fa-arrow-left" aria-hidden />
            <span>Voltar ao estudante</span>
          </button>
        </div>

        {studentId && packageId && (
          <div className="mb-6">
            <StudentPackageAccessSection studentId={studentId} packageId={packageId} />
          </div>
        )}

        <div className="mb-6 rounded-xl border border-neutral-200 bg-gradient-to-r from-white to-blue-50/40 p-5 shadow-sm">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div className="space-y-1">
              <h2 className="text-lg font-semibold text-neutral-900">Controle de acesso por módulo</h2>
              <p className="text-sm text-neutral-800">
                Gerencie bloqueio, progresso e reset de respostas por módulo deste pacote.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              <div className="rounded-lg border border-neutral-200 bg-white px-3 py-2 text-center">
                <p className="text-xs font-medium uppercase tracking-wide text-neutral-700">Total</p>
                <p className="text-lg font-semibold text-neutral-900">{modules.length}</p>
              </div>
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-center">
                <p className="text-xs font-medium uppercase tracking-wide text-emerald-800">Liberados</p>
                <p className="text-lg font-semibold text-emerald-900">{unlockedModulesCount}</p>
              </div>
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-center">
                <p className="text-xs font-medium uppercase tracking-wide text-amber-800">Bloqueados</p>
                <p className="text-lg font-semibold text-amber-900">{modules.length - unlockedModulesCount}</p>
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4 text-sm font-medium text-red-800">
            {error}
          </div>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <div className="h-12 w-12 animate-spin rounded-full border-2 border-neutral-200 border-t-blue-600" aria-label="Carregando" />
          </div>
        ) : modules.length === 0 ? (
          <div className="rounded-lg border border-neutral-200 bg-white p-8 text-center text-sm text-neutral-800">
            Nenhum módulo neste pacote.
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
            {modules.map((mod) => (
              <Card
                key={mod.moduleId}
                className="rounded-xl border border-neutral-200 bg-white p-6 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
              >
                <div className="flex flex-col gap-6">
                  <div className="flex items-start justify-between gap-4 border-b border-neutral-100 pb-5">
                    <div className="min-w-0 flex-1">
                      <h3 className="text-base font-semibold text-neutral-900">{mod.title}</h3>
                      {mod.internalName && (
                        <p className="mt-1 text-xs font-medium text-neutral-600">Nome interno: {mod.internalName}</p>
                      )}
                    </div>
                      <div className="flex flex-shrink-0 flex-col items-end gap-2 sm:flex-row sm:items-center">
                        {(() => {
                          switch (mod.moduleTestProgress) {
                            case ModuleTestProgress.Completed:
                              return (
                                <div
                                  className="relative inline-flex h-10 overflow-hidden rounded-lg shadow-sm transition-all hover:shadow-md"
                                  role="group"
                                  aria-label={`Ações do teste de módulo: ${mod.title}`}
                                >
                                  <Link
                                    to={`/admin/access/students/${studentId}/packages/${packageId}/modules/${mod.moduleId}/module-test`}
                                    className="inline-flex items-center justify-center gap-2 bg-[#10b981] px-4 text-sm font-semibold text-[#064e3b] transition-colors hover:bg-[#059669] hover:text-white active:scale-95"
                                    aria-label={`Ver relatório do teste de módulo ${mod.title}`}
                                  >
                                    <i className="fa-solid fa-eye shrink-0" aria-hidden />
                                    Ver teste
                                  </Link>
                                  <div className="w-[1px] bg-[#064e3b]/10"></div>
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <button
                                        type="button"
                                        className="inline-flex w-10 shrink-0 items-center justify-center rounded-r-lg rounded-l-none bg-[#064e3b] text-white transition-colors hover:bg-black active:scale-95"
                                        aria-label={`Mais ações de teste do módulo ${mod.title}`}
                                      >
                                        <ChevronDown className="h-4 w-4 shrink-0" aria-hidden />
                                      </button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent
                                      align="end"
                                      className="min-w-[12rem]"
                                      onCloseAutoFocus={(e) => {
                                        if (skipTestMenuCloseFocus.current) {
                                          e.preventDefault();
                                          skipTestMenuCloseFocus.current = false;
                                        }
                                      }}
                                    >
                                      <DropdownMenuItem
                                        className="cursor-pointer text-destructive focus:bg-red-50 focus:text-destructive"
                                        onSelect={() => {
                                          skipTestMenuCloseFocus.current = true;
                                          window.setTimeout(() => handleRemoveTestClick(mod), 0);
                                        }}
                                      >
                                        <i className="fa-solid fa-trash-can" aria-hidden />
                                        Remover teste
                                      </DropdownMenuItem>
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </div>
                              );
                            case ModuleTestProgress.InProgress:
                              return (
                                <Button
                                  type="button"
                                  variant="outline"
                                  className="h-10 min-h-[44px] border-red-200 text-destructive hover:border-red-300 hover:bg-red-50 hover:text-destructive"
                                  onClick={() => handleRemoveTestClick(mod)}
                                  aria-label={`Remover teste de módulo em andamento: ${mod.title}`}
                                >
                                  <i className="fa-solid fa-trash-can mr-2" aria-hidden />
                                  Remover teste
                                </Button>
                              );
                            case ModuleTestProgress.NotStarted:
                              return null;
                            default:
                              devWarnUnknownModuleTestProgress(mod.moduleTestProgress, mod.title);
                              return null;
                          }
                        })()}
                        <Button
                          variant="outline"
                          size="default"
                          className="h-10 min-w-[44px] border-amber-200 text-amber-700 hover:bg-amber-50 hover:text-amber-800 hover:border-amber-300"
                          onClick={() => handleResetClick(mod)}
                          aria-label={`Resetar respostas do módulo ${mod.title}`}
                        >
                          <i className="fa-solid fa-rotate-left mr-2" aria-hidden />
                          Resetar
                        </Button>
                      </div>
                  </div>

                  <div
                    className="pt-1"
                    role="group"
                    aria-labelledby={`lock-status-${mod.moduleId}`}
                    aria-busy={lockTogglingModuleId === mod.moduleId}
                  >
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div className="space-y-0.5">
                        <span
                          id={`lock-status-${mod.moduleId}`}
                          className="text-sm font-semibold text-neutral-900"
                        >
                          Visibilidade do módulo
                        </span>
                        <p className="text-xs text-neutral-500">
                          Defina se este conteúdo está disponível.
                        </p>
                      </div>
                      <div
                        className={`inline-flex w-full min-w-[240px] items-center rounded-xl bg-neutral-100 p-1 shadow-inner sm:mt-0.5 sm:w-auto ${
                          lockTogglingModuleId === mod.moduleId ? "opacity-70" : ""
                        }`}
                        role="group"
                        aria-label={`Visibilidade do módulo ${mod.title}`}
                      >
                        <button
                          type="button"
                          className={`flex flex-1 items-center justify-center gap-2 rounded-l-lg rounded-r-none px-4 py-2.5 text-sm font-semibold transition-all ${
                            mod.isUnlocked
                              ? "bg-neutral-100 text-neutral-500 hover:text-neutral-700"
                              : "bg-white text-red-700 shadow-sm ring-1 ring-black/5"
                          }`}
                          disabled={lockTogglingModuleId === mod.moduleId || !mod.isUnlocked}
                          aria-pressed={!mod.isUnlocked}
                          aria-label={`Bloquear módulo ${mod.title}`}
                          onClick={() => handleLockToggle(mod, false)}
                        >
                          <i
                            className={`fa-solid fa-lock ${mod.isUnlocked ? "text-neutral-400" : "text-red-700"}`}
                            aria-hidden
                          />
                          Bloqueado
                        </button>
                        <button
                          type="button"
                          className={`flex flex-1 items-center justify-center gap-2 rounded-r-lg rounded-l-none px-4 py-2.5 text-sm font-semibold transition-all ${
                            mod.isUnlocked
                              ? "bg-white text-emerald-700 shadow-sm ring-1 ring-black/5"
                              : "bg-neutral-100 text-neutral-500 hover:text-neutral-700"
                          }`}
                          disabled={lockTogglingModuleId === mod.moduleId || !!mod.isUnlocked}
                          aria-pressed={!!mod.isUnlocked}
                          aria-label={`Liberar módulo ${mod.title}`}
                          onClick={() => handleLockToggle(mod, true)}
                        >
                          <i className={`fa-solid fa-lock-open ${mod.isUnlocked ? "text-emerald-700" : "text-neutral-400"}`} aria-hidden />
                          Liberado
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-sm font-medium text-neutral-800">Progresso</span>
                      <span className="text-sm font-semibold text-neutral-900">
                        {mod.progressPercentage != null ? `${formatProgress(mod.progressPercentage)}%` : "—"}
                      </span>
                    </div>
                    <div
                      className="h-2 w-full overflow-hidden rounded-full bg-neutral-200"
                      role="progressbar"
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-valuenow={formatProgress(mod.progressPercentage)}
                      aria-label={`Progresso do módulo ${mod.title}`}
                    >
                      <div
                        className={`h-full rounded-full transition-all ${
                          formatProgress(mod.progressPercentage) >= 80
                            ? "bg-emerald-500"
                            : formatProgress(mod.progressPercentage) >= 40
                              ? "bg-blue-500"
                              : "bg-amber-500"
                        }`}
                        style={{ width: `${formatProgress(mod.progressPercentage)}%` }}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-3 border-t border-neutral-100 pt-4">
                    <div className="grid grid-cols-[1fr_auto] items-center gap-x-4 gap-y-1 text-sm">
                      <span className="text-neutral-800">Exercícios</span>
                      <span className="font-medium text-neutral-900">
                        {mod.exercisesAnswered} / {mod.totalExercises}
                      </span>
                    </div>
                    <div className="grid grid-cols-[1fr_auto] items-center gap-x-4 gap-y-1 text-sm">
                      <span className="text-neutral-800">Última atividade</span>
                      <span className="font-medium text-neutral-900">
                        {formatLastActivity(mod.lastActivityAt)}
                      </span>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        <Dialog open={resetModalOpen} onOpenChange={(open) => !open && handleResetCancel()}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Resetar respostas do módulo</DialogTitle>
              <DialogDescription className="text-sm text-neutral-800">
                Isso apagará todas as respostas do módulo &quot;{moduleToReset?.title}&quot;. O
                progresso deste módulo será zerado e o estudante poderá refazer os exercícios.
                Deseja continuar?
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" className="min-h-[44px] px-4" onClick={handleResetCancel} disabled={isResetting}>
                Cancelar
              </Button>
              <Button
                variant="destructive"
                className="min-h-[44px] px-4"
                onClick={handleResetConfirm}
                disabled={isResetting}
              >
                {isResetting ? (
                  <>
                    <i className="fa-solid fa-spinner fa-spin mr-2" aria-hidden />
                    Resetando…
                  </>
                ) : (
                  "Resetar respostas"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={removeTestModalOpen} onOpenChange={(open) => !open && handleRemoveTestCancel()}>
          <DialogContent
            className="sm:max-w-[520px]"
            onCloseAutoFocus={(e) => e.preventDefault()}
          >
            <DialogHeader>
              <DialogTitle>Remover teste do módulo</DialogTitle>
              <DialogDescription className="text-sm text-neutral-800">
                Isso removerá o histórico do teste deste módulo para este estudante (sessões, respostas e registros relacionados).
                O estudante poderá refazer o teste do zero. Deseja continuar?
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" className="min-h-[44px] px-4" onClick={handleRemoveTestCancel} disabled={isRemovingTest}>
                Cancelar
              </Button>
              <Button
                variant="destructive"
                className="min-h-[44px] px-4"
                onClick={handleRemoveTestConfirm}
                disabled={isRemovingTest}
              >
                {isRemovingTest ? (
                  <>
                    <i className="fa-solid fa-spinner fa-spin mr-2" aria-hidden />
                    Removendo…
                  </>
                ) : (
                  "Remover teste"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </AdminPageLayout>
  );
};

export default StudentPackageManagementPage;
